// Trang Tổng quan tài liệu: bộ lọc kỳ + đơn vị dùng chung, 5 tab phân tích (Tổng quan, Mức độ đọc, Quy trình duyệt,
// Hiệu lực & rà soát, Đa chiều) và xuất báo cáo Excel gồm toàn bộ phân tích.
import { useMemo } from 'react';
import { Button, Segmented, Select, Tabs, message } from 'antd';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Download, List } from 'lucide-react';
import DocShell from './components/DocShell';
import useDocsStore from './store';
import { DEPARTMENTS, deptName, type Doc } from './data';
import { canViewDoc } from './logic';
import { EXPIRING_DAYS, FLAG_LABEL, PERIOD_LABEL, periodRange, type Flag, type PeriodKey } from './stats';
import {
  DIMENSION_LABEL, accessSeries, approvalAnalytics, dimensionAnalytics, lifecycleAnalytics, overviewAnalytics, readingAnalytics, type Dimension,
} from './analytics';
import { useDocAuth, usePeople } from './utils';
import type { DashCtx } from './dashboard/ui';
import OverviewTab from './dashboard/OverviewTab';
import ReadingTab from './dashboard/ReadingTab';
import ApprovalTab from './dashboard/ApprovalTab';
import LifecycleTab from './dashboard/LifecycleTab';
import DimensionsTab from './dashboard/DimensionsTab';

type View = 'overview' | 'reading' | 'approval' | 'lifecycle' | 'dimensions';
const VIEWS: { key: View; label: string }[] = [
  { key: 'overview', label: 'Tổng quan' },
  { key: 'reading', label: 'Mức độ đọc' },
  { key: 'approval', label: 'Quy trình duyệt' },
  { key: 'lifecycle', label: 'Hiệu lực & rà soát' },
  { key: 'dimensions', label: 'Phân tích đa chiều' },
];

export default function DocumentDashboard() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { viewer, can } = useDocAuth();
  const people = usePeople();
  const allDocs = useDocsStore(s => s.docs);
  const types = useDocsStore(s => s.types);
  const categories = useDocsStore(s => s.categories);

  const view = (VIEWS.find(v => v.key === params.get('view'))?.key || 'overview') as View;
  const period = (params.get('period') as PeriodKey) in PERIOD_LABEL ? (params.get('period') as PeriodKey) : '30d';
  const unit = params.get('unit') || '';
  const dim = ((params.get('dim') as Dimension) in DIMENSION_LABEL ? params.get('dim') : 'type') as Dimension;
  const setParam = (k: string, v: string, fallback = '') => {
    const next = new URLSearchParams(params);
    if (v && v !== fallback) next.set(k, v); else next.delete(k);
    setParams(next, { replace: true });
  };
  /** Mở màn Quản lý với bộ lọc, giữ đơn vị đang chọn */
  const goList = (q: Record<string, string>) => {
    const sp = new URLSearchParams(q);
    if (unit && !sp.has('unit')) sp.set('unit', unit);
    navigate(`/document-management?${sp.toString()}`);
  };

  const ctx: DashCtx = useMemo(() => {
    const now = dayjs();
    const scoped = allDocs.filter(d => canViewDoc(d, viewer, people) && (!unit || d.unitId === unit));
    return {
      docs: scoped.filter(d => !d.deletedAt), scoped, allDocs, people, types, categories,
      p: periodRange(period, now), now, period, unit, goList,
      setPeriod: (k: PeriodKey) => setParam('period', k, '30d'),
    };
    // goList/setParam phụ thuộc params — ctx chỉ cần tính lại khi dữ liệu hoặc bộ lọc đổi
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDocs, viewer, people, types, categories, period, unit, params]);

  const overview = useMemo(() => overviewAnalytics(ctx.docs, ctx.scoped, ctx.allDocs, people, ctx.p, ctx.now), [ctx, people]);
  const reading = useMemo(() => readingAnalytics(ctx.docs, people, types, ctx.p, ctx.now), [ctx, people, types]);
  const access = useMemo(() => accessSeries(ctx.docs, ctx.p), [ctx]);
  const approval = useMemo(() => approvalAnalytics(ctx.docs, ctx.p, ctx.now), [ctx]);
  const lifecycle = useMemo(() => lifecycleAnalytics(ctx.docs, categories, ctx.p, ctx.now), [ctx, categories]);
  const dimRows = useMemo(() => dimensionAnalytics(ctx.docs, people, categories, types, dim, ctx.p, ctx.now), [ctx, people, categories, types, dim]);

  const exportReport = () => {
    const wb = XLSX.utils.book_new();
    const pct = (x: number) => `${Math.round(x * 100)}%`;
    const days = (x: number) => Number(x.toFixed(2));
    const add = (name: string, rows: object[]) => XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows.length ? rows : [{ 'Ghi chú': 'Không có dữ liệu' }]), name);
    const o = overview;
    add('Tong quan', [
      { 'Chỉ số': 'Kỳ báo cáo', 'Kỳ này': `${ctx.p.start.format('DD/MM/YYYY')} – ${ctx.p.end.format('DD/MM/YYYY')}`, 'Kỳ trước': `${ctx.p.prevStart.format('DD/MM/YYYY')} – ${ctx.p.prevEnd.format('DD/MM/YYYY')}` },
      { 'Chỉ số': 'Đơn vị', 'Kỳ này': unit ? deptName(unit) : 'Tất cả', 'Kỳ trước': '' },
      { 'Chỉ số': 'Đang hiệu lực', 'Kỳ này': o.active, 'Kỳ trước': o.activePrev },
      { 'Chỉ số': 'Ban hành mới', 'Kỳ này': o.issued, 'Kỳ trước': o.issuedPrev },
      { 'Chỉ số': 'Chờ duyệt', 'Kỳ này': o.pending, 'Kỳ trước': '' },
      { 'Chỉ số': 'Tỷ lệ đã đọc', 'Kỳ này': pct(o.readNow), 'Kỳ trước': pct(o.readPrev) },
      { 'Chỉ số': `Sắp hết hiệu lực (${EXPIRING_DAYS} ngày)`, 'Kỳ này': o.expiring.length, 'Kỳ trước': '' },
      { 'Chỉ số': 'Thời gian duyệt TB (ngày)', 'Kỳ này': days(o.slaAvg), 'Kỳ trước': days(o.slaPrevAvg) },
    ]);
    add('Can xu ly', ([['overdue', o.overdue], ['expiring', o.expiring], ['lowread', o.lowread], ['comments', o.comments]] as [Flag, Doc[]][])
      .flatMap(([f, list]) => list.map(d => ({ 'Cần xử lý': FLAG_LABEL[f], 'Mã tài liệu': d.code, 'Tên tài liệu': d.title, 'Đơn vị ban hành': deptName(d.unitId), 'Người tạo': d.createdByName })))
      .concat(o.rejected.map(d => ({ 'Cần xử lý': 'Bị từ chối, chưa gửi lại', 'Mã tài liệu': d.code, 'Tên tài liệu': d.title, 'Đơn vị ban hành': deptName(d.unitId), 'Người tạo': d.createdByName }))));
    add('Muc do doc', [
      { 'Chỉ số': 'Tài liệu ban hành trong kỳ', 'Giá trị': reading.cohort },
      { 'Chỉ số': 'Tỷ lệ đã đọc', 'Giá trị': pct(reading.rate) },
      { 'Chỉ số': 'Đọc trong 24 giờ', 'Giá trị': pct(reading.within24) },
      { 'Chỉ số': 'Thời gian đọc trung vị (ngày)', 'Giá trị': days(reading.medianDays) },
      { 'Chỉ số': 'Lượt chưa đọc', 'Giá trị': reading.unread },
      ...reading.buckets.map(b => ({ 'Chỉ số': `Đọc: ${b.label}`, 'Giá trị': b.count })),
    ]);
    add('Doc theo phong ban', o.depts.map(x => ({ 'Phòng ban': x.name, 'Lượt được chia sẻ': x.total, 'Đã đọc': x.read, 'Tỷ lệ': pct(x.rate) })));
    add('Tai lieu it doc', reading.lowDocs.map(x => ({ 'Mã tài liệu': x.doc.code, 'Tên tài liệu': x.doc.title, 'Đã đọc': x.read, 'Người nhận': x.total, 'Tỷ lệ': pct(x.rate), 'Ban hành (ngày trước)': x.ageDays })));
    add('Quy trinh duyet', [
      { 'Chỉ số': 'Thời gian duyệt TB (ngày)', 'Giá trị': days(approval.avgDays) },
      { 'Chỉ số': 'Trung vị (ngày)', 'Giá trị': days(approval.medianDays) },
      { 'Chỉ số': 'P90 (ngày)', 'Giá trị': days(approval.p90Days) },
      { 'Chỉ số': 'Đạt mục tiêu', 'Giá trị': pct(approval.sla) },
      { 'Chỉ số': 'Tỷ lệ từ chối', 'Giá trị': pct(approval.rejectRate) },
      { 'Chỉ số': 'Duyệt ngay lần đầu', 'Giá trị': pct(approval.firstPass) },
      ...approval.steps.map(s => ({ 'Chỉ số': `Bước: ${s.label} (ngày)`, 'Giá trị': days(s.days) })),
    ]);
    add('Nguoi duyet', approval.approvers.map(a => ({ 'Người duyệt': a.name, 'Ban hành': a.approved, 'Từ chối': a.rejected, 'TG xử lý TB (ngày)': days(a.avgDays), 'Đạt mục tiêu': pct(a.sla) })));
    add('Can ra soat', lifecycle.review.map(r => ({ 'Mã tài liệu': r.doc.code, 'Tên tài liệu': r.doc.title, 'Đơn vị': deptName(r.doc.unitId), 'Cập nhật cuối (tháng trước)': r.months })));
    add('Lich hieu luc', lifecycle.timeline.map(t => ({ 'Ngày': dayjs(t.date).format('DD/MM/YYYY'), 'Sự kiện': t.kind === 'expire' ? 'Hết hiệu lực' : 'Bắt đầu hiệu lực', 'Mã tài liệu': t.doc.code, 'Tên tài liệu': t.doc.title })));
    (Object.keys(DIMENSION_LABEL) as Dimension[]).forEach(d => {
      add(`Theo ${DIMENSION_LABEL[d]}`.slice(0, 31), dimensionAnalytics(ctx.docs, people, categories, types, d, ctx.p, ctx.now).map(r => ({
        [DIMENSION_LABEL[d]]: r.label, 'Tổng': r.total, 'Hiệu lực': r.active, 'Ban hành kỳ này': r.issued, 'Chờ duyệt': r.pending,
        'Tỷ lệ đọc': r.readPairs ? pct(r.readRate) : '', 'TG duyệt TB (ngày)': r.decisions ? days(r.avgApproval) : '', 'Tỷ lệ từ chối': r.decisions ? pct(r.rejectRate) : '', 'Sắp hết hạn': r.expiring,
      })));
    });
    XLSX.writeFile(wb, `bao-cao-tai-lieu_${dayjs().format('YYYYMMDD_HHmm')}.xlsx`);
    message.success('Đã xuất báo cáo đầy đủ');
  };

  return (
    <DocShell
      title="Tổng quan tài liệu"
      subtitle={`${ctx.p.start.format('DD/MM/YYYY')} – ${ctx.p.end.format('DD/MM/YYYY')} · so với ${ctx.p.prevStart.format('DD/MM/YYYY')} – ${ctx.p.prevEnd.format('DD/MM/YYYY')}`}
      actions={<>
        <Button icon={<List size={15} />} onClick={() => goList({})}>Danh sách tài liệu</Button>
        {can('export') && <Button icon={<Download size={15} />} onClick={exportReport}>Xuất báo cáo</Button>}
      </>}
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Segmented<PeriodKey> value={period} onChange={v => setParam('period', v, '30d')} options={(Object.keys(PERIOD_LABEL) as PeriodKey[]).map(k => ({ value: k, label: PERIOD_LABEL[k] }))} />
        <Select value={unit} onChange={v => setParam('unit', v)} className="w-52" options={[{ value: '', label: 'Tất cả đơn vị ban hành' }, ...DEPARTMENTS.map(d => ({ value: d.id, label: d.name }))]} />
      </div>
      <Tabs
        activeKey={view} onChange={k => setParam('view', k, 'overview')} className="[&_.ant-tabs-nav]:!mb-4"
        items={VIEWS.map(v => ({ key: v.key, label: v.label }))}
      />
      {view === 'overview' && <OverviewTab ctx={ctx} data={overview} />}
      {view === 'reading' && <ReadingTab ctx={ctx} data={reading} access={access} />}
      {view === 'approval' && <ApprovalTab ctx={ctx} data={approval} />}
      {view === 'lifecycle' && <LifecycleTab ctx={ctx} data={lifecycle} />}
      {view === 'dimensions' && <DimensionsTab ctx={ctx} dim={dim} setDim={d => setParam('dim', d, 'type')} rows={dimRows} />}
    </DocShell>
  );
}
